import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CompleteIcon,
  Cancel as DismissIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import TasksFilters from '../../components/Tasks/TasksFilters';
import TaskForm from '../../components/Tasks/TaskForm';
import TaskDetails from '../../components/Tasks/TaskDetails';
import { 
  getPriorityColor, 
  getStatusColor,
  getEntityTypeColor,
  getPriorityLabel,
  getStatusLabel,
  getEntityTypeLabel,
  getTaskTypeLabel
} from '../../constants/tasksConstants';
import { Link as RouterLink } from 'react-router-dom';

const TasksPage = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Stan filtrów
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: isAdmin ? 'all' : currentUser.uid,
    entityType: 'all',
    taskType: 'all',
    priority: 'all',
    status: 'all',
    createdBy: 'all',
    entityId: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Pobierz dane przy pierwszym renderowaniu
  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchLeads();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin, currentUser]);

  // Filtruj zadania przy zmianie filtrów
  useEffect(() => {
    applyFilters();
  }, [tasks, filters]);

  // Funkcja pobierająca zadania
  const fetchTasks = async () => {
    try {
      setLoading(true);
      let tasksQuery;
      
      if (isAdmin) {
        // Administrator widzi wszystkie zadania
        tasksQuery = query(
          collection(db, 'tasks'),
          orderBy('dueDate', 'asc')
        );
      } else {
        // Zwykły pracownik widzi tylko swoje zadania
        tasksQuery = query(
          collection(db, 'tasks'),
          where('assignedTo', '==', currentUser.uid),
          orderBy('dueDate', 'asc')
        );
      }
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const tasksList = await Promise.all(tasksSnapshot.docs.map(async doc => {
        const taskData = doc.data();
        
        // Pobierz dane o podmiocie (klient/lead)
        let entityName = '';
        if (taskData.entityType !== 'general' && taskData.entityId) {
          try {
            const entityRef = doc(db, taskData.entityType === 'client' ? 'clients' : 'leads', taskData.entityId);
            const entityDoc = await getDoc(entityRef);
            if (entityDoc.exists()) {
              const entityData = entityDoc.data();
              entityName = `${entityData.firstName || ''} ${entityData.lastName || ''}`.trim();
            }
          } catch (error) {
            console.error(`Błąd podczas pobierania danych podmiotu: ${error}`);
          }
        }
        
        // Pobierz dane o przypisanym pracowniku
        let assignedToName = '';
        try {
          const userRef = doc(db, 'users', taskData.assignedTo);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            assignedToName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          }
        } catch (error) {
          console.error(`Błąd podczas pobierania danych pracownika: ${error}`);
        }
        
        // Pobierz dane o twórcy zadania (jeśli nie jest to AI)
        let createdByName = '';
        if (taskData.createdBy !== 'ai_system') {
          try {
            const creatorRef = doc(db, 'users', taskData.createdBy);
            const creatorDoc = await getDoc(creatorRef);
            if (creatorDoc.exists()) {
              const creatorData = creatorDoc.data();
              createdByName = `${creatorData.firstName || ''} ${creatorData.lastName || ''}`.trim();
            }
          } catch (error) {
            console.error(`Błąd podczas pobierania danych twórcy: ${error}`);
          }
        }
        
        return {
          id: doc.id,
          ...taskData,
          dueDate: taskData.dueDate?.toDate() || new Date(),
          createdAt: taskData.createdAt?.toDate() || new Date(),
          lastUpdate: taskData.lastUpdate?.toDate() || new Date(),
          completedAt: taskData.completedAt?.toDate() || null,
          entityName,
          assignedToName,
          createdByName
        };
      }));
      
      setTasks(tasksList);
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas pobierania zadań:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania zadań',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Funkcja pobierająca klientów
  const fetchClients = async () => {
    try {
      const clientsQuery = query(collection(db, 'clients'));
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName || '',
        lastName: doc.data().lastName || ''
      }));
      setClients(clientsList);
    } catch (error) {
      console.error('Błąd podczas pobierania klientów:', error);
      setClients([]);
    }
  };

  // Funkcja pobierająca leady
  const fetchLeads = async () => {
    try {
      const leadsQuery = query(collection(db, 'leads'));
      const leadsSnapshot = await getDocs(leadsQuery);
      const leadsList = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName || '',
        lastName: doc.data().lastName || ''
      }));
      setLeads(leadsList);
    } catch (error) {
      console.error('Błąd podczas pobierania leadów:', error);
      setLeads([]);
    }
  };

  // Funkcja pobierająca pracowników
  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName || '',
        lastName: doc.data().lastName || '',
        email: doc.data().email || '',
        role: doc.data().role || 'user'
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
      setEmployees([]);
    }
  };

  // Funkcja filtrująca zadania
  const applyFilters = () => {
    let filtered = [...tasks];
    
    // Filtrowanie po wyszukiwaniu
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(task => 
        task.description?.toLowerCase().includes(searchLower) || 
        task.suggestedAction?.toLowerCase().includes(searchLower) ||
        task.entityName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtrowanie po przypisanym pracowniku
    if (filters.assignedTo !== 'all') {
      filtered = filtered.filter(task => task.assignedTo === filters.assignedTo);
    }
    
    // Filtrowanie po typie podmiotu
    if (filters.entityType !== 'all') {
      filtered = filtered.filter(task => task.entityType === filters.entityType);
    }
    
    // Filtrowanie po typie zadania
    if (filters.taskType !== 'all') {
      filtered = filtered.filter(task => task.taskType === filters.taskType);
    }
    
    // Filtrowanie po priorytecie
    if (filters.priority !== 'all') {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }
    
    // Filtrowanie po statusie
    if (filters.status !== 'all') {
      filtered = filtered.filter(task => task.status === filters.status);
    }
    
    // Filtrowanie po twórcy
    if (filters.createdBy !== 'all') {
      if (filters.createdBy === 'me') {
        filtered = filtered.filter(task => task.createdBy === currentUser.uid);
      } else if (filters.createdBy === 'ai_system') {
        filtered = filtered.filter(task => task.createdBy === 'ai_system');
      } else if (filters.createdBy === 'other') {
        filtered = filtered.filter(task => 
          task.createdBy !== currentUser.uid && task.createdBy !== 'ai_system'
        );
      }
    }
    
    // Filtrowanie po konkretnym podmiocie
    if (filters.entityId !== 'all') {
      filtered = filtered.filter(task => task.entityId === filters.entityId);
    }
    
    // Filtrowanie po zakresie dat
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(task => task.dueDate >= fromDate);
    }
    
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(task => task.dueDate <= toDate);
    }
    
    // Sortowanie po priorytecie i dacie wykonania
    filtered.sort((a, b) => {
      // Najpierw po priorytecie (high > medium > low)
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      
      if (priorityDiff !== 0) return priorityDiff;
      
      // Następnie po dacie wykonania (rosnąco)
      return a.dueDate - b.dueDate;
    });
    
    setFilteredTasks(filtered);
    // Reset paginacji przy zmianie filtrów
    setPage(0);
  };

  // Obsługa zmiany filtrów
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Obsługa paginacji
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Obsługa oznaczania zadania jako ukończone
  const handleCompleteTask = async (taskId, notes) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        status: 'completed',
        completedAt: new Date(),
        notesOnCompletion: notes,
        lastUpdate: new Date()
      });
      
      // Odśwież listę zadań
      fetchTasks();
      
      setSnackbar({
        open: true,
        message: 'Zadanie zostało oznaczone jako ukończone',
        severity: 'success'
      });
    } catch (error) {
      console.error('Błąd podczas oznaczania zadania jako ukończone:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas aktualizacji zadania',
        severity: 'error'
      });
    }
  };

  // Obsługa odrzucania zadania
  const handleDismissTask = async (taskId, notes) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        status: 'dismissed',
        completedAt: new Date(),
        notesOnCompletion: notes,
        lastUpdate: new Date()
      });
      
      // Odśwież listę zadań
      fetchTasks();
      
      setSnackbar({
        open: true,
        message: 'Zadanie zostało odrzucone',
        severity: 'success'
      });
    } catch (error) {
      console.error('Błąd podczas odrzucania zadania:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas aktualizacji zadania',
        severity: 'error'
      });
    }
  };

  // Obsługa dodawania/edycji zadania
  const handleSaveTask = (taskData) => {
    // Odśwież listę zadań
    fetchTasks();
    
    // Zamknij dialog
    setFormDialogOpen(false);
    
    setSnackbar({
      open: true,
      message: selectedTask ? 'Zadanie zostało zaktualizowane' : 'Nowe zadanie zostało dodane',
      severity: 'success'
    });
    
    // Wyczyść wybrane zadanie
    setSelectedTask(null);
  };

  // Obsługa dodawania kontaktu z poziomu szczegółów zadania
  const handleAddContact = (entityType, entityId) => {
    // Tutaj można by przekierować do formularza dodawania kontaktu
    // z predefiniowanymi wartościami entityType i entityId
    console.log('Dodawanie kontaktu dla:', entityType, entityId);
    // Można by użyć np. history.push z react-router-dom
  };

  // Format daty
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd.MM.yyyy', { locale: pl });
  };

  // Skrócenie tekstu
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Nagłówek */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Zadania</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedTask(null);
            setFormDialogOpen(true);
          }}
        >
          Dodaj zadanie
        </Button>
      </Box>

      {/* Filtry */}
      <TasksFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        employees={employees}
        clients={clients}
        leads={leads}
        isAdmin={isAdmin}
        currentUserId={currentUser?.uid}
      />

      {/* Tabela zadań */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Priorytet</TableCell>
                <TableCell>Data wykonania</TableCell>
                <TableCell>Podmiot</TableCell>
                <TableCell>Typ zadania</TableCell>
                <TableCell>Opis</TableCell>
                {isAdmin && <TableCell>Przypisane do</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                    Brak zadań spełniających kryteria
                  </TableCell>
                </TableRow>
              ) : (
                // Wyświetl przefiltrowane i spaginowane zadania
                filteredTasks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((task) => {
                    const canEdit = isAdmin || task.assignedTo === currentUser?.uid;
                    const isCompleted = task.status === 'completed';
                    const isDismissed = task.status === 'dismissed';
                    const isActive = !isCompleted && !isDismissed;
                    
                    return (
                      <TableRow key={task.id} hover>
                        {/* Priorytet */}
                        <TableCell>
                          <Chip 
                            label={getPriorityLabel(task.priority)} 
                            color={getPriorityColor(task.priority)}
                            size="small"
                          />
                        </TableCell>
                        
                        {/* Data wykonania */}
                        <TableCell>{formatDate(task.dueDate)}</TableCell>
                        
                        {/* Podmiot */}
                        <TableCell>
                          {task.entityType === 'general' ? (
                            <Chip 
                              label="Ogólne" 
                              color="default" 
                              size="small"
                            />
                          ) : task.entityType === 'client' ? (
                            <Link component={RouterLink} to={`/clients/${task.entityId}`}>
                              {task.entityName || task.entityId}
                            </Link>
                          ) : (
                            <Link component={RouterLink} to={`/leads/${task.entityId}`}>
                              {task.entityName || task.entityId}
                            </Link>
                          )}
                        </TableCell>
                        
                        {/* Typ zadania */}
                        <TableCell>{getTaskTypeLabel(task.taskType)}</TableCell>
                        
                        {/* Opis */}
                        <TableCell>{truncateText(task.description, 80)}</TableCell>
                        
                        {/* Przypisane do (tylko dla admina) */}
                        {isAdmin && (
                          <TableCell>{task.assignedToName || task.assignedTo}</TableCell>
                        )}
                        
                        {/* Status */}
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(task.status)} 
                            color={getStatusColor(task.status)}
                            size="small"
                          />
                        </TableCell>
                        
                        {/* Akcje */}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {/* Przycisk szczegółów */}
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setSelectedTask(task);
                                setDetailsDialogOpen(true);
                              }}
                              size="small"
                              title="Szczegóły"
                            >
                              <ViewIcon />
                            </IconButton>
                            
                            {/* Przycisk edycji (tylko dla aktywnych zadań) */}
                            {isActive && canEdit && (
                              <IconButton
                                color="secondary"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setFormDialogOpen(true);
                                }}
                                size="small"
                                title="Edytuj"
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            
                            {/* Przycisk ukończenia (tylko dla aktywnych zadań) */}
                            {isActive && canEdit && (
                              <IconButton
                                color="success"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setDetailsDialogOpen(true);
                                }}
                                size="small"
                                title="Oznacz jako ukończone"
                              >
                                <CompleteIcon />
                              </IconButton>
                            )}
                            
                            {/* Przycisk odrzucenia (tylko dla aktywnych zadań) */}
                            {isActive && canEdit && (
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setDetailsDialogOpen(true);
                                }}
                                size="small"
                                title="Odrzuć zadanie"
                              >
                                <DismissIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Paginacja */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTasks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
        />
      </Paper>

      {/* Dialog dodawania/edycji zadania */}
      <Dialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedTask(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTask ? 'Edytuj zadanie' : 'Dodaj nowe zadanie'}
        </DialogTitle>
        <DialogContent>
          <TaskForm
            initialData={selectedTask}
            onSubmit={handleSaveTask}
            onCancel={() => {
              setFormDialogOpen(false);
              setSelectedTask(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog szczegółów zadania */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedTask(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedTask && (
            <TaskDetails
              task={selectedTask}
              onUpdate={(updatedTask) => {
                fetchTasks();
                setDetailsDialogOpen(false);
                setSelectedTask(null);
              }}
              onEdit={() => {
                setDetailsDialogOpen(false);
                setFormDialogOpen(true);
              }}
              onAddContact={handleAddContact}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar do powiadomień */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TasksPage;
